/**
 * View Service - Read-only views with custom queries
 *
 * Supports:
 * - Read all (paginated)
 * - Read by ID
 * - Custom query builder or SQL VIEW
 */

import type {
  ViewEntityDefinition,
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks
} from '@gzl10/nexus-sdk'
import type { EntityQuery } from '@kernel/runtime/index.js'
import { BaseEntityService } from './base.service.js'
import { registerLiveObserver } from '../helpers/live-observer.js'
import type { EntityRepository } from '../repositories/types.js'

export class ViewService<T extends Record<string, unknown> = Record<string, unknown>>
  extends BaseEntityService<T> {

  declare readonly definition: ViewEntityDefinition

  constructor(ctx: ModuleContext, definition: ViewEntityDefinition, hooks?: EntityServiceHooks<T>, repo?: EntityRepository) {
    super(ctx, definition, hooks, repo)

    // Register live mode observer: push updates when source tables change
    if (definition.realtime === 'live' && definition.liveOn?.length) {
      const events = definition.liveOn.flatMap(table => [
        `db.${table}.created`, `db.${table}.updated`, `db.${table}.deleted`
      ])
      registerLiveObserver({
        ctx, events,
        moduleName: this.moduleName, entityKey: this.entityKey, label: definition.label
      })
    }
  }

  /**
   * Get all items with pagination.
   *
   * When definition.query is a function (custom Knex builder), the query is
   * executed directly because KnexViewRepository only supports named views/tables.
   *
   * LIMITATION: Custom query-builder path bypasses the repo layer. This is
   * intentional — the repo interface only accepts a view/table name; supporting
   * arbitrary builders would require extending IViewRepository.
   */
  async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    if (this.definition.query && typeof this.definition.query === 'function') {
      return this.findAllWithQueryBuilder(query)
    }

    // Standard path: delegate to repo (KnexViewRepository or injected mock)
    return this.baseFindAll(query)
  }

  /**
   * Get item by ID.
   *
   * LIMITATION: Custom query-builder path bypasses the repo layer for the same
   * reason as findAll — the builder is not accessible via IViewRepository.
   */
  async findById(id: string): Promise<T | null> {
    if (this.definition.query && typeof this.definition.query === 'function') {
      const entity = await this.definition.query(this.db)
        .where('id', id)
        .first<T>()

      return this.processAfterFindById(entity ?? null)
    }

    // Standard path: delegate to repo
    return this.baseFindById(id)
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  /**
   * findAll using a custom Knex query builder from definition.query.
   * Called only when definition.query is a function.
   */
  private async findAllWithQueryBuilder(query?: EntityQuery): Promise<PaginatedResult<T>> {
    const { page, limit, offset } = this.getPagination(query)
    // Cast is safe: this method is only called after verifying definition.query is a function
    const queryFn = this.definition.query as (db: import('knex').Knex) => import('knex').Knex.QueryBuilder

    let baseQuery = queryFn(this.db)

    // Apply search
    if (query?.search && this.definition.labelField) {
      baseQuery = baseQuery.where(this.definition.labelField, 'like', `%${query.search}%`)
    }

    // Apply filters (reuse applyFilters for full operator support)
    if (query?.filters) {
      baseQuery = this.applyFilters(baseQuery.clone(), query.filters)
    }

    // Get count (clone baseQuery which already has search/filters applied)
    const countResult = await baseQuery.clone().count('* as count').first<{ count: string | number }>()
    const total = Number(countResult?.count ?? 0)

    // Apply sorting
    if (query?.sort) {
      baseQuery = baseQuery.orderBy(query.sort, query.order ?? 'asc')
    } else if (this.definition.labelField) {
      baseQuery = baseQuery.orderBy(this.definition.labelField, 'asc')
    }

    // Apply pagination
    baseQuery = baseQuery.limit(limit).offset(offset)

    const items = await baseQuery as T[]

    return this.buildPaginatedResult(items, total, page, limit)
  }

  // ============================================================================
  // SQL VIEW management
  // ============================================================================

  /**
   * Create SQL VIEW from definition (for migrations)
   */
  async createView(): Promise<void> {
    const query = this.definition.query

    if (!query) {
      this.logger.warn({ table: this.table }, 'No query defined for view')
      return
    }

    let sql: string

    if (typeof query === 'string') {
      sql = query
    } else {
      // Build query and convert to SQL
      const qb = query(this.db)
      sql = qb.toQuery()
    }

    // Create or replace view
    // Note: SQL is either a static string from entity definition (developer-authored, trusted)
    // or built via Knex query builder (parameterized). Never from user input.
    await this.db.raw(`CREATE OR REPLACE VIEW ?? AS ${sql}`, [this.table])

    this.logger.info({ table: this.table }, 'View created')
  }

  /**
   * Drop SQL VIEW
   */
  async dropView(): Promise<void> {
    await this.db.raw('DROP VIEW IF EXISTS ??', [this.table])
    this.logger.info({ table: this.table }, 'View dropped')
  }

  // ============================================================================
  // Read-only enforcement
  // ============================================================================

  /**
   * Views are read-only
   */
  async create(_data: Partial<T>): Promise<T> {
    throw new this.errors.ForbiddenError('Views are read-only')
  }

  async update(_id: string, _data: Partial<T>): Promise<T> {
    throw new this.errors.ForbiddenError('Views are read-only')
  }

  async delete(_id: string): Promise<void> {
    throw new this.errors.ForbiddenError('Views are read-only')
  }
}
